import React, { useMemo, useState } from 'react';

/**
 * BigSkyEnrichmentShell
 *
 * Inline a-la-carte attribute picker rendered inside VertebraConfigModal
 * for the bigsky_enrichment vertebra type. The "Enrich From" selector and
 * the source Brain-field picker are rendered ABOVE this component in the
 * modal (mirrors the suppression pattern); this shell owns the big
 * attribute catalog — grouped, searchable, with per-group select-all.
 *
 * The catalog below is PROVISIONAL. The live DataMoon/BigSky identity
 * payload shape isn't fully enumerated in the vendor docs, so these keys
 * are seeded from the vendor's filter dictionary + restricted-output list.
 * Once we capture a real enrichment response, replace BIGSKY_ATTRIBUTE_
 * CATALOG with the actual returned field set (keys must match the vendor's
 * identity object keys exactly — the backend maps selected keys straight
 * onto the returned identity).
 */

export interface CatalogGroup {
  group: string;
  attributes: Array<{ key: string; label: string }>;
}

// PROVISIONAL — see file header. Keys are the vendor attribute names the
// backend will read off the returned identity object.
export const BIGSKY_ATTRIBUTE_CATALOG: CatalogGroup[] = [
  {
    group: 'Identity',
    attributes: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'full_name', label: 'Full Name' },
      { key: 'gender', label: 'Gender' },
      { key: 'age_range', label: 'Age Range' },
      { key: 'linkedin_url', label: 'LinkedIn URL' },
    ],
  },
  {
    group: 'Contact',
    attributes: [
      { key: 'personal_emails', label: 'Personal Email' },
      { key: 'business_email', label: 'Business Email' },
      { key: 'personal_phone', label: 'Phone' },
      { key: 'mobile_phone', label: 'Mobile Phone' },
      // Phone line detail (canonical keys — the backend maps the vendor identity
      // through mapRetargetiqLiveRecord and selects by canonical key).
      { key: 'mobile_carrier', label: 'Mobile Carrier' },
      { key: 'mobile_dnc', label: 'Mobile on DNC (Do Not Call)' },
      { key: 'phone_type', label: 'Phone Type (primary)' },
      { key: 'personal_phone_types', label: 'Phone Types (all)' },
      { key: 'personal_phone_carriers', label: 'Phone Carriers (all)' },
      { key: 'personal_phone_dnc', label: 'Phone DNC Flags (all)' },
    ],
  },
  {
    group: 'Location',
    attributes: [
      { key: 'personal_address', label: 'Address' },
      { key: 'personal_address_2', label: 'Address 2' },
      { key: 'personal_city', label: 'City' },
      { key: 'personal_state', label: 'State' },
      { key: 'personal_zip', label: 'Zip' },
      { key: 'personal_zip4', label: 'Zip+4' },
      { key: 'contact_country', label: 'Country' },
    ],
  },
  {
    group: 'Household & Demographics',
    attributes: [
      { key: 'married', label: 'Married' },
      { key: 'children', label: 'Children' },
      { key: 'homeowner', label: 'Homeowner' },
      { key: 'income_range', label: 'Income Range' },
      { key: 'net_worth', label: 'Net Worth' },
    ],
  },
  {
    group: 'Professional',
    attributes: [
      { key: 'job_title', label: 'Job Title' },
      { key: 'seniority_level', label: 'Seniority Level' },
      { key: 'department', label: 'Department' },
    ],
  },
  {
    group: 'Company',
    attributes: [
      { key: 'company_name', label: 'Company Name' },
      { key: 'company_domain', label: 'Company URL' },
      { key: 'primary_industry', label: 'Industry' },
      { key: 'company_revenue', label: 'Company Revenue' },
      { key: 'company_employee_count', label: 'Company Size' },
    ],
  },
];

interface Props {
  selected: string[];
  onChange: (next: string[]) => void;
  /** Attribute catalog to render. Defaults to the BigSky catalog; the owned
   *  enrichment vertebra passes our canonical catalog instead. */
  catalog?: CatalogGroup[];
  /** Show the "list is provisional" amber notice (true for BigSky's
   *  not-yet-confirmed catalog; owned passes false — built from real data). */
  showProvisionalNote?: boolean;
  /** Show the "billed only for attributes enriched" note (off for the pixel editor). */
  showBillingNote?: boolean;
}

export function BigSkyEnrichmentShell({ selected, onChange, catalog: catalogProp, showProvisionalNote = true, showBillingNote = true }: Props) {
  const catalog = catalogProp ?? BIGSKY_ATTRIBUTE_CATALOG;
  const allKeys = useMemo(() => catalog.flatMap(g => g.attributes.map(a => a.key)), [catalog]);
  const [search, setSearch] = useState('');
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const q = search.trim().toLowerCase();
  const groups = useMemo(() => {
    if (!q) {return catalog;}
    return catalog
      .map(g => ({
        group: g.group,
        attributes: g.attributes.filter(
          a => a.label.toLowerCase().includes(q) || a.key.toLowerCase().includes(q)
        ),
      }))
      .filter(g => g.attributes.length > 0);
  }, [q, catalog]);

  const toggle = (key: string) => {
    if (selectedSet.has(key)) {onChange(selected.filter(k => k !== key));}
    else {onChange([...selected, key]);}
  };

  const setGroup = (keys: string[], on: boolean) => {
    if (on) {
      const merged = new Set([...selected, ...keys]);
      onChange(Array.from(merged));
    } else {
      const remove = new Set(keys);
      onChange(selected.filter(k => !remove.has(k)));
    }
  };

  const selectAll = () => onChange([...allKeys]);
  const clearAll = () => onChange([]);

  return (
    <div style={{ marginTop: 8 }}>
      {/* Provisional-catalog notice */}
      {showProvisionalNote && <div style={{
        background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6,
        padding: '10px 14px', fontSize: 12, lineHeight: 1.5, color: '#78350f', marginBottom: 12,
      }}>
        <strong>Attribute list is provisional.</strong> These are the attributes we expect to be
        available. The exact set is finalized from live results — a few may change once the first
        real enrichments come back.
      </div>}

      {/* Billing note */}
      {showBillingNote && <div style={{
        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6,
        padding: '10px 14px', fontSize: 12, lineHeight: 1.5, color: '#1e3a8a', marginBottom: 16,
      }}>
        You&apos;re billed <strong>only for attributes actually enriched</strong> — i.e. an attribute
        you enable here that comes back populated on a given lead. Enabling an attribute that the
        vendor can&apos;t fill for a lead costs nothing. The field you enrich <em>from</em> is never billed.
      </div>}

      {/* Toolbar: search + select-all/clear + count */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search attributes…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 160, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
        />
        <button type="button" className="btn btn-secondary btn-sm" onClick={selectAll}>Select all</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={clearAll}>Clear</button>
        <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
          <strong style={{ color: '#4f46e5' }}>{selected.length}</strong> selected
        </span>
      </div>

      {/* Grouped checkboxes */}
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, maxHeight: 380, overflowY: 'auto' }}>
        {groups.length === 0 ? (
          <div style={{ padding: 16, fontSize: 13, color: '#9ca3af' }}>No attributes match &ldquo;{search}&rdquo;.</div>
        ) : (
          groups.map(g => {
            const keys = g.attributes.map(a => a.key);
            const allOn = keys.every(k => selectedSet.has(k));
            const someOn = keys.some(k => selectedSet.has(k));
            return (
              <div key={g.group} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 14px', background: '#f8f9fb', position: 'sticky', top: 0,
                }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280' }}>
                    {g.group}
                  </span>
                  <button
                    type="button"
                    onClick={() => setGroup(keys, !allOn)}
                    style={{ background: 'none', border: 'none', color: '#4f46e5', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    {allOn ? 'Clear group' : someOn ? 'Select rest' : 'Select group'}
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 4, padding: '8px 12px' }}>
                  {g.attributes.map(a => {
                    const on = selectedSet.has(a.key);
                    return (
                      <label key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
                        <input type="checkbox" checked={on} onChange={() => toggle(a.key)} style={{ flexShrink: 0 }} />
                        <span style={{ color: on ? '#111827' : '#374151', fontWeight: on ? 600 : 400 }}>{a.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default BigSkyEnrichmentShell;
